import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { QRCodeSVG } from "qrcode.react";
import { Plus, TriangleAlert } from "lucide-react";
import { SwapOrder } from "../stores/SwapOrder";
import { AppWorkspaceContext } from "../stores/AppWorkspace";
import {
  APP_PAGES,
  formatTimeLeft,
  satsToBtc,
  assetName,
  assetDepositLabel,
  attentionMessage,
  emergencyExplanation,
  coinFromAssetCode
} from "../helpers/constants";
import { refundAddressError } from "../helpers/chainAddress";
import { explorerTxUrl, shortTxId } from "../helpers/chainExplorers";
import { iconForSymbol } from "../helpers/cryptoIcons";
import { Alert, Badge, Button, Field, IconCopyButton, Input, Panel } from "../../ui";

const PHASE_STEPS = [
  "awaiting_deposit",
  "confirming",
  "exchanging",
  "paying_invoice",
  "completed"
] as const;

const phaseIndex = (phase: string): number => {
  const idx = PHASE_STEPS.indexOf(phase as typeof PHASE_STEPS[number]);
  if (idx >= 0) return idx;
  if (["refund_required", "refund_pending", "refunded"].includes(phase)) return 1;
  if (phase === "expired" || phase === "failed" || phase === "attention") return -1;
  return 0;
};

/** mm:ss for the deposit window — always two digits so the clock doesn't jump. */
const formatCountdown = (seconds: number): string => {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

interface OrderDetailProps {
  order: SwapOrder;
  /** Hide list-oriented chrome when this is the only swap on screen. */
  standalone?: boolean;
}

const CopyField: React.FC<{ label: string; value: string | null | undefined }> = ({
  label,
  value
}) => {
  if (!value) return null;
  const selectAll = (e: React.SyntheticEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };
  return (
    <Field label={label}>
      <div className="flex items-start gap-2">
        <Input
          readOnly
          value={value}
          className="min-w-0 flex-1 font-mono"
          onFocus={selectAll}
          onClick={selectAll}
          onMouseUp={(e) => e.preventDefault()}
        />
        <IconCopyButton value={value} />
      </div>
    </Field>
  );
};

/** Truncated tx id → block explorer when the pay-in chain has one (not LN). */
const TxRef: React.FC<{ txid: string; assetCode: string; prefix?: string }> = ({
  txid,
  assetCode,
  prefix = "tx "
}) => {
  const url = explorerTxUrl(txid, assetCode);
  const label = `${prefix}${shortTxId(txid)}`;
  if (!url) return <span>{label}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
      title="Open on block explorer"
    >
      {label}
    </a>
  );
};

const OrderDetail: React.FC<OrderDetailProps> = observer(({ order, standalone = false }) => {
  const workspace = useContext(AppWorkspaceContext);
  const [refundAddress, setRefundAddress] = useState("");
  const [refundMemo, setRefundMemo] = useState("");
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const currentStep = phaseIndex(order.phase);
  const secondsLeft = Math.max(order.expiresAt - now, 0);
  const depositAsset = coinFromAssetCode(order.fromCode).toUpperCase();
  const refundError = refundAddress.trim()
    ? refundAddressError(order.fromCode, refundAddress)
    : null;
  const showDeposit = Boolean(order.depositAddress && order.acceptsDeposit);
  // A kept overpay completed the swap: never tell the payer they sent too
  // much or to submit a refund, whatever emergency code may sit on the row.
  const emergency =
    order.emergencyStatus.length > 0 && !order.overpayKept
      ? emergencyExplanation(order.emergencyStatus, order.refundReason, {
          receivedAmount: order.depositTxAmount,
          assetLabel: depositAsset,
          refundLapsed: order.refundLapsed
        })
      : null;
  // "deposit_after_payout" on a kept overpay with nothing received since is a
  // stale flag on a finished swap, not something the payer must act on.
  const showAttention =
    order.attention && !(order.overpayKept && order.attentionReason === "deposit_after_payout");

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const confirmingDetail = (() => {
    const conf =
      order.depositConfirmations != null
        ? `${order.depositConfirmations} / ${order.reqConfirmations} confirmations`
        : `Waiting for ${order.reqConfirmations} confirmations`;
    if (!order.depositTxId) return conf;
    return (
      <>
        {conf}
        {" · "}
        <TxRef txid={order.depositTxId} assetCode={order.fromCode} />
      </>
    );
  })();

  const steps: { title: string; detail: React.ReactNode }[] = [
    {
      title: "Awaiting deposit",
      detail: `Send ${order.depositAmount} ${depositAsset} to the deposit address.`
    },
    {
      title: "Confirming",
      detail: confirmingDetail
    },
    {
      title: "Exchanging",
      detail: `Converting ${assetName(order.fromCode)} to Lightning liquidity.`
    },
    {
      title: "Paying invoice",
      // payoutTxId is a Lightning payment hash — no on-chain explorer.
      detail:
        "Settling your BOLT11 payout." +
        (order.payoutTxId ? ` · ${shortTxId(order.payoutTxId)}` : "")
    },
    {
      title: "Completed",
      detail:
        order.phase === "refunded" && order.refundTxId ? (
          <>
            Swap finished (refunded)
            {" · "}
            <TxRef txid={order.refundTxId} assetCode={order.fromCode} />
          </>
        ) : (
          `Swap finished${order.phase === "refunded" ? " (refunded)" : ""}.`
        )
    }
  ];

  return (
    <Panel>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <img
                src={iconForSymbol(coinFromAssetCode(order.fromCode))}
                alt=""
                width={24}
                height={24}
              />
              <h2 className="font-display text-xl font-semibold">{order.providerOrderId}</h2>
              <Badge tone={order.phaseColor as "info"}>{order.phaseLabel}</Badge>
            </div>
            <p className="text-sm text-muted">
              {assetName(order.fromCode)} → Bitcoin Lightning ·{" "}
              {order.amountSats.toLocaleString()} sats ({satsToBtc(order.amountSats)} BTC)
            </p>
          </div>
        </div>

        {showAttention && (
          <Alert tone="danger" title="Needs manual review" icon={<TriangleAlert size={16} />}>
            <p className="text-sm">{attentionMessage(order.attentionReason)}</p>
            <p className="mt-1 text-xs opacity-80">
              Quote order ID <strong>{order.providerOrderId}</strong> when you contact support.
            </p>
          </Alert>
        )}

        {showDeposit && secondsLeft > 0 && (
          <div className="rounded-lg border border-border bg-paper px-4 py-5 sm:px-5">
            <Alert
              tone="danger"
              title="Wrong currency or network = lost funds"
              icon={<TriangleAlert size={20} />}
              className="mb-5"
            >
              <p className="text-sm font-semibold">
                Be sure you are sending exactly{" "}
                <strong className="underline decoration-2 underline-offset-2">
                  {order.depositAmount} {assetDepositLabel(order.fromCode)}
                </strong>
                . If you send the wrong currency or send on the wrong network,
                your funds will be lost!
              </p>
            </Alert>

            <div className="mb-5 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[10rem] flex-1">
                    <CopyField label="Send exactly" value={order.depositAmount} />
                  </div>
                  <span className="mb-2 text-lg font-semibold text-muted">{depositAsset}</span>
                </div>
                <p className="mt-1 text-sm text-muted">
                  on {assetName(order.fromCode)}. Send exactly this amount — too little or too
                  much cannot be swapped and must be refunded.
                </p>
              </div>
              <div
                className={`text-right ${
                  secondsLeft > 60 ? "text-ink" : "text-danger"
                }`}
              >
                <div className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Time left
                </div>
                <div className="font-mono text-3xl font-bold tabular-nums sm:text-4xl">
                  {formatCountdown(secondsLeft)}
                </div>
                <div className="text-xs text-muted">
                  Deposit expires in {formatTimeLeft(secondsLeft)}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
              <div className="shrink-0 rounded-lg border border-border bg-white p-3">
                <QRCodeSVG
                  value={order.depositAddress!}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#1c1917"
                  level="M"
                />
              </div>
              <div className="flex w-full min-w-0 flex-1 flex-col gap-3">
                <CopyField label="Deposit address" value={order.depositAddress} />
                {order.depositMemo && (
                  <CopyField label="Memo / tag (required)" value={order.depositMemo} />
                )}
                <p className="text-xs text-muted">
                  Scan the QR or paste the address into your {depositAsset} wallet, then send{" "}
                  <strong className="text-ink">
                    {order.depositAmount} {depositAsset}
                  </strong>{" "}
                  before the timer hits zero.
                </p>
                <p className="text-xs text-muted">
                  By initiating a swap, you confirm that you agree with our{" "}
                  <a href="/terms" target="_blank" rel="noreferrer">Terms</a>.
                </p>
              </div>
            </div>
          </div>
        )}

        {showDeposit && secondsLeft <= 0 && (
          <Alert tone="danger" title="Deposit window closed">
            <p className="text-sm">
              Do not send to this address. Open a new swap if you still want to pay that invoice.
            </p>
          </Alert>
        )}

        {!showDeposit && order.phase === "awaiting_deposit" && (
          <Alert tone="warn" title="Deposit address not ready">
            Refresh this page in a moment. If it stays missing, save your swap token and contact
            support with order <strong>{order.providerOrderId}</strong>.
          </Alert>
        )}

        {!(showDeposit && secondsLeft <= 0) && (
        <div>
          <div className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-muted">
            Progress
          </div>
          <ol className="space-y-3">
            {steps.map((step, idx) => {
              const done = currentStep > idx;
              const active = currentStep === idx;
              return (
                <li key={step.title} className="flex gap-3">
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      done || active
                        ? "bg-accent text-accent-ink"
                        : "bg-border text-muted"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <div>
                    <div className={`text-sm font-semibold ${active ? "text-accent" : "text-ink"}`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-muted">{step.detail}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
        )}

        {order.phase === "completed" && (
          <Button
            leftIcon={<Plus size={16} />}
            onClick={() => workspace.setPage(APP_PAGES.NEW_SWAP)}
            className="self-start"
          >
            Start a new swap
          </Button>
        )}

        {emergency && (
          <Alert tone="warn" title={emergency.title}>
            <p className="text-sm">{emergency.body}</p>
            {order.depositTxId && (
              <p className="mt-2 text-sm">
                Deposit: <TxRef txid={order.depositTxId} assetCode={order.fromCode} />
              </p>
            )}
          </Alert>
        )}

        {order.phase === "refund_pending" && (
          <Alert tone="warn" title="Refund in progress">
            <p className="text-sm">
              Your refund is being broadcast. This page updates automatically —
              please do not resubmit.
              {order.refundTxId && (
                <>
                  {" "}
                  Tracking: <TxRef txid={order.refundTxId} assetCode={order.fromCode} />
                </>
              )}
            </p>
          </Alert>
        )}

        {order.phase === "refunded" && order.refundTxId && (
          <Alert tone="info" title="Refund sent">
            <p className="text-sm">
              Refund transaction: <TxRef txid={order.refundTxId} assetCode={order.fromCode} />
            </p>
          </Alert>
        )}

        {order.needsRefund && (
          <div className="flex flex-col gap-3">
            <div className="text-center text-xs font-semibold uppercase tracking-wide text-muted">
              Refund
            </div>
            <p className="text-sm">
              Submit a refund address on the <strong>same network</strong> as your deposit (
              {assetName(order.fromCode)}).
            </p>
            {order.refundClaimUntil && (
              <p className="text-sm">
                Request it by <strong>{new Date(order.refundClaimUntil * 1000).toLocaleString()}</strong>.
                After that the deposit is no longer refundable.
              </p>
            )}
            <Field label="Refund address" error={refundError}>
              <Input
                placeholder="Your on-chain address"
                value={refundAddress}
                error={refundError}
                onChange={(e) => setRefundAddress(e.currentTarget.value)}
              />
            </Field>
            <Field label="Memo (if required)">
              <Input
                value={refundMemo}
                onChange={(e) => setRefundMemo(e.currentTarget.value)}
              />
            </Field>
            <Button
              variant="warn"
              onClick={() => workspace.submitRefund(order, refundAddress, refundMemo)}
              loading={workspace.areWeWaitingOnRails}
              disabled={refundAddressError(order.fromCode, refundAddress) !== null}
            >
              Submit refund address
            </Button>
          </div>
        )}

        {!standalone && (
          <div>
            <Button variant="ghost" size="xs" onClick={() => workspace.selectOrder(null)}>
              Close detail
            </Button>
          </div>
        )}
      </div>
    </Panel>
  );
});

export default OrderDetail;
